import { useEffect, useState } from "react";
import { Bookmark } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Universal save toggle for any item type.
 *
 * Props
 *  - itemType  one of: "tryon" | "outfit" | "community_post" | "atelier" | "stylist" | "event_outfit"
 *  - itemId    unique id
 *  - snapshot  light dict of fields to preserve in the saved view (image, title, etc.)
 *  - initialSaved  optional bool — if known up-front avoids the GET roundtrip
 *  - variant   "icon" (default, just a button) | "chip" (icon + label) | "ghost" (overlay corner button)
 *  - className passthrough
 *  - testId    optional override
 */
export default function SaveButton({
  itemType,
  itemId,
  snapshot = {},
  initialSaved,
  variant = "icon",
  className = "",
  testId,
}) {
  const [saved, setSaved] = useState(initialSaved ?? false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(initialSaved !== undefined);

  useEffect(() => {
    if (checked || !itemId) return;
    api.get("/saved/check", { params: { item_type: itemType, item_ids: itemId } })
      .then(({ data }) => setSaved(!!data[itemId]))
      .catch(() => {})
      .finally(() => setChecked(true));
  }, [itemType, itemId, checked]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (busy || !itemId) return;
    setBusy(true);
    const next = !saved;
    setSaved(next); // optimistic
    try {
      if (next) {
        await api.post("/saved", { item_type: itemType, item_id: itemId, snapshot });
        toast.success("Saved");
      } else {
        await api.delete(`/saved/${itemType}/${itemId}`);
        toast.success("Removed from saved");
      }
    } catch {
      setSaved(!next); // rollback
      toast.error("Could not update");
    } finally {
      setBusy(false);
    }
  };

  const id = testId || `save-${itemType}-${itemId}`;

  if (variant === "chip") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={id}
        aria-pressed={saved}
        className={`inline-flex items-center gap-2 px-3 h-9 border text-[11px] uppercase tracking-[0.18em] transition-colors ${
          saved ? "bg-accent text-accent-foreground border-accent" : "border-border hover:border-foreground"
        } ${className}`}
      >
        <Bookmark size={13} fill={saved ? "currentColor" : "none"} />
        {saved ? "Saved" : "Save"}
      </button>
    );
  }

  if (variant === "ghost") {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={id}
        aria-label={saved ? "Remove from saved" : "Save"}
        aria-pressed={saved}
        className={`absolute top-3 right-3 z-10 h-9 w-9 inline-flex items-center justify-center backdrop-blur transition-colors ${
          saved ? "bg-accent text-accent-foreground" : "bg-background/85 text-foreground hover:bg-accent hover:text-accent-foreground"
        } ${className}`}
      >
        <Bookmark size={15} fill={saved ? "currentColor" : "none"} />
      </button>
    );
  }

  // icon
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={id}
      aria-label={saved ? "Remove from saved" : "Save"}
      aria-pressed={saved}
      className={`p-2 transition-colors ${saved ? "text-accent" : "text-muted-foreground hover:text-foreground"} ${className}`}
    >
      <Bookmark size={16} fill={saved ? "currentColor" : "none"} />
    </button>
  );
}
